import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * TableOfContents Component
 * Sticky sidebar that displays and links to article sections
 * Improves dwell time - a Google ranking factor
 */
export const TableOfContents = ({ article }) => {
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  // Extract sections from article structure
  useEffect(() => {
    const extractedSections = [];

    // Always include main sections
    if (article.title) {
      extractedSections.push({
        id: 'overview',
        title: 'Overview',
        level: 0
      });
    }

    if (article.quickAnswer) {
      extractedSections.push({
        id: 'quick-answer',
        title: 'Quick Answer',
        level: 0
      });
    }

    if (article.expandedAnswer) {
      extractedSections.push({
        id: 'in-detail',
        title: 'In Detail',
        level: 0
      });
    }

    if (article.steps && article.steps.length > 0) {
      extractedSections.push({
        id: 'step-by-step-guide',
        title: 'Step-by-Step Guide',
        level: 0
      });

      article.steps.forEach((step, index) => {
        extractedSections.push({
          id: `step-${index}`,
          title: `Step ${index + 1}: ${step.name}`,
          level: 1
        });
      });
    }

    if (article.faqItems && article.faqItems.length > 0) {
      extractedSections.push({
        id: 'people-also-ask',
        title: 'People Also Ask',
        level: 0
      });
    }

    if (article.category) {
      extractedSections.push({
        id: 'related-articles',
        title: 'Related Articles',
        level: 0
      });
    }

    setSections(extractedSections);
  }, [article]);

  // Handle scroll to smoothly navigate to sections
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);

    // Find the corresponding element and scroll to it
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Track active section as user scrolls
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const elementTop = element.getBoundingClientRect().top + window.scrollY;
          const elementBottom = elementTop + element.offsetHeight;

          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  if (sections.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      className="w-full"
    >
      <div className="max-h-72 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10">
        {/* Header - Unified with other sidebar components */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full mb-4 group"
        >
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider group-hover:text-white transition-colors">
            Table of Contents
          </h3>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground group-hover:text-white transition-colors" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground group-hover:text-white transition-colors" />
          )}
        </button>

        {/* Sections List */}
        {isExpanded && (
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-0.5"
          >
            {sections.map((section) => (
              <motion.button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center gap-2 w-full text-left text-sm py-2 px-2 rounded-lg transition-all ${activeSection === section.id
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-white hover:bg-white/5'
                  } ${section.level === 1 ? 'ml-3 text-xs' : ''}`}
                whileHover={{ x: 2 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${activeSection === section.id ? 'bg-primary' : 'bg-white/20'
                  }`} />
                <span className="line-clamp-1">{section.title}</span>
              </motion.button>
            ))}
          </motion.nav>
        )}

        {/* Collapse indicator */}
        {!isExpanded && (
          <div className="text-xs text-muted-foreground text-center py-1">
            Click to expand
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TableOfContents;
